import { useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult } from "@tanstack/react-query"
import ResponseError from "../../../types/responseError"
import { carrotClient } from "../../../utils/CarrotClient"
import buildCarrotApiRetryPolicy from "../../../utils/CarrotApiRetryPolicy"
import { reportErrorMessage } from "../../../utils/ErrorReporting"
import { getCarrotPlanQueryKey } from "../hooks/useCarrotPlan"
import { useCurrentUser } from "../../context/user/UserContext"
import { useMedicalInfo } from "../context/MedicalInfoContext"
import { useHistory } from "react-router-dom"
import { ROUTING_INFO_QUERY_KEY, useStartRoutingInfo } from "../shared/useQueryRoutingInfo"
import { AdvancedCheckInEmailLinkQueryString } from "./useCaseRateRenewals"

export enum CaseRateRenewalsStatus {
  NONE = "NONE",
  GATED = "GATED",
  NOT_GATED = "NOT_GATED",
  ADVANCED_CHECK_IN_GATED = "ADVANCED_CHECK_IN_GATED",
  ADVANCED_CHECK_IN_DISMISSED = "ADVANCED_CHECK_IN_DISMISSED"
}

export const caseRateRenewalStatusQueryKey = "caseRateRenewalStatus"

export const daysSinceLastUnlockQueryKey = "getDaysSinceLastUnlock"

export const hasCarrotPlanCreatedWithin90DaysOfUnlockAnniversaryQueryKey =
  "hasCarrotPlanCreatedWithin90DaysOfUnlockAnniversary"

export function useGetCaseRateRenewalStatus(queryStringStep?: string | string[]) {
  const { employeeId } = useCurrentUser()
  const caseRateRenewalStatusQuery = useQuery<CaseRateRenewalsStatus, ResponseError>(
    [caseRateRenewalStatusQueryKey],
    () => carrotClient.getCaseRateRenewalStatus(),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: buildCarrotApiRetryPolicy(3),
      onError: (error) =>
        reportErrorMessage(`Could not load case rate renewal status for member ${employeeId} error: ${error}`)
    }
  )

  const showAdvancedCheckInFeatures =
    caseRateRenewalStatusQuery.data === CaseRateRenewalsStatus.ADVANCED_CHECK_IN_GATED ||
    caseRateRenewalStatusQuery.data === CaseRateRenewalsStatus.ADVANCED_CHECK_IN_DISMISSED

  const showAdvancedCheckFromLinkInEmail =
    caseRateRenewalStatusQuery.data === CaseRateRenewalsStatus.ADVANCED_CHECK_IN_DISMISSED &&
    (queryStringStep === AdvancedCheckInEmailLinkQueryString.KEEP_PLAN ||
      queryStringStep === AdvancedCheckInEmailLinkQueryString.REFRESH_PLAN)

  const shouldCrrModalRender =
    caseRateRenewalStatusQuery.data === CaseRateRenewalsStatus.GATED ||
    caseRateRenewalStatusQuery.data === CaseRateRenewalsStatus.ADVANCED_CHECK_IN_GATED ||
    showAdvancedCheckFromLinkInEmail

  const isCrrAdvancedCheckIn =
    caseRateRenewalStatusQuery.data === CaseRateRenewalsStatus.ADVANCED_CHECK_IN_GATED ||
    showAdvancedCheckFromLinkInEmail

  return {
    ...caseRateRenewalStatusQuery,
    showAdvancedCheckInFeatures: showAdvancedCheckInFeatures,
    shouldCrrModalRender: shouldCrrModalRender,
    isCrrAdvancedCheckIn: isCrrAdvancedCheckIn
  }
}

export function useRepublishCarrotPlan(): UseMutationResult {
  const { employeeId, refreshUserInfo } = useCurrentUser()
  const queryClient = useQueryClient()
  return useMutation(() => carrotClient.republishCarrotPlan(employeeId), {
    retry: buildCarrotApiRetryPolicy(1),
    onSuccess: async () => {
      await queryClient.refetchQueries(getCarrotPlanQueryKey())
      await queryClient.refetchQueries(["GetCarrotPlanEmployeeSettings"])
      await queryClient.refetchQueries([caseRateRenewalStatusQueryKey])
      await refreshUserInfo()
    }
  })
}

export function useSetDoneWithCarrot(nonStaleCheckIn?: boolean): UseMutationResult {
  const { refreshUserInfo } = useCurrentUser()
  const queryClient = useQueryClient()
  return useMutation(() => carrotClient.setDoneWithCarrot(), {
    retry: buildCarrotApiRetryPolicy(1),
    onSuccess: async () => {
      await queryClient.refetchQueries(getCarrotPlanQueryKey())
      await queryClient.refetchQueries(["GetCarrotPlanEmployeeSettings"])
      await queryClient.refetchQueries([caseRateRenewalStatusQueryKey])
      if (!nonStaleCheckIn) {
        await refreshUserInfo()
      }
    }
  })
}

export function useRequestNewCaseRateRenewalCarrotPlan(): UseMutationResult {
  const { refreshUserInfo } = useCurrentUser()
  const queryClient = useQueryClient()
  return useMutation(() => carrotClient.requestNewCaseRateRenewalCarrotPlan(), {
    retry: buildCarrotApiRetryPolicy(1),
    onSuccess: async () => {
      await queryClient.refetchQueries(["GetCarrotPlanEmployeeSettings"])
      await queryClient.refetchQueries(getCarrotPlanQueryKey())
      await queryClient.refetchQueries([caseRateRenewalStatusQueryKey])
      await refreshUserInfo()
    }
  })
}

export function useGetHasCarrotPlanCreatedWithin90DaysOfUnlockAnniversary() {
  return useQuery<CaseRateRenewalsStatus, ResponseError>(
    [hasCarrotPlanCreatedWithin90DaysOfUnlockAnniversaryQueryKey],
    () => carrotClient.getHasCarrotPlanCreatedWithin90DaysOfUnlockAnniversary(),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: buildCarrotApiRetryPolicy(3)
    }
  )
}

export function useSetNonStaleCarrotPlanOnAnniversaryCheck(): UseMutationResult {
  const queryClient = useQueryClient()
  return useMutation(() => carrotClient.setNonStaleCarrotPlanOnAnniversaryCheck(), {
    retry: buildCarrotApiRetryPolicy(1),
    onSuccess: async () => {
      await queryClient.refetchQueries([caseRateRenewalStatusQueryKey])
    }
  })
}

export function useStartCaseRateRenewalRouting() {
  const { mutate } = useStartRoutingInfo()
  const queryClient = useQueryClient()
  const { refetch: refetchMedicalInfo } = useMedicalInfo()
  const history = useHistory()

  async function startCaseRateRenewalRouting() {
    mutate(null, {
      onSuccess: async () => {
        // Remove these query from the cache
        queryClient.removeQueries(["carrotPlansMedicalInfo"])
        // Refetch so that we are sure to get null back for medical since we are starting routing over
        await queryClient.prefetchQuery([ROUTING_INFO_QUERY_KEY])
        await refetchMedicalInfo()
        history.push("/carrot-plans/case-rate-renewal/routing/?refreshRequest=true")
      }
    })
  }

  return { startCaseRateRenewalRouting }
}

export function useGetDaysSinceLastUnlock(): UseQueryResult<number, ResponseError> {
  return useQuery<number, ResponseError>([daysSinceLastUnlockQueryKey], () => carrotClient.getDaysSinceLastUnlock(), {
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: false,
    retry: buildCarrotApiRetryPolicy(3)
  })
}

export function useSetCarrotPlanRenewalDismissed(): UseMutationResult {
  const queryClient = useQueryClient()
  return useMutation(() => carrotClient.setCarrotPlanRenewalDismissed(), {
    retry: buildCarrotApiRetryPolicy(1),
    onSuccess: async () => {
      await queryClient.refetchQueries([caseRateRenewalStatusQueryKey])
    }
  })
}
